import React, { useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import SLABadge from "@/components/SLABadge";
import ComplaintTimeline from "@/components/ComplaintTimeline";
import { STATUS_LABELS, STATUS_COLORS } from "@/lib/constants";
import { Search } from "lucide-react";
import { toast } from "sonner";

export default function TrackComplaint() {
  const [ref, setRef] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    if (!ref.trim()) return;
    setBusy(true);
    setData(null);
    try {
      const { data } = await api.get(`/complaints/by-reference/${ref.trim().toUpperCase()}`);
      setData(data);
    } catch (err) {
      toast.error(err?.response?.status === 404 ? "No complaint found" : "Search failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="overline text-slate-500 mb-2">Public tracking</div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Track a complaint</h1>
          <p className="text-slate-600 mt-2 text-sm">Enter your reference number (e.g. NGP-XXXXXXXX) to see the current status.</p>
        </div>
        <Card>
          <CardHeader><CardTitle className="font-display">Search</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={search} className="flex gap-3" data-testid="track-form">
              <Input placeholder="NGP-XXXXXXXX" value={ref} onChange={(e) => setRef(e.target.value)} data-testid="track-input" />
              <Button type="submit" disabled={busy} className="gap-2" data-testid="track-submit-btn">
                <Search className="w-4 h-4" /> {busy ? "Searching..." : "Search"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {data && (
          <Card className="mt-6">
            <CardHeader>
              <div className="overline text-slate-500">{data.reference}</div>
              <CardTitle className="font-display" data-testid="tracked-title">{data.title}</CardTitle>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Badge variant="outline" className={STATUS_COLORS[data.status] || ""}>
                  {STATUS_LABELS[data.status]}
                </Badge>
                <SLABadge
                  createdAt={data.created_at}
                  levelStartedAt={data.level_started_at}
                  currentLevel={data.current_level}
                  status={data.status}
                  escalated={data.escalated}
                />
                <span className="text-xs text-muted-foreground">Zone: {data.zone_name}</span>
              </div>
            </CardHeader>
            <CardContent>
              <ComplaintTimeline timeline={data.timeline || []} />
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
